import { X, Truck, ArrowRight, ExternalLink, Video, CloudSun } from "lucide-react";
import { money } from "@/lib/format";
import { STAGE_COLORS } from "@/components/map/FlowLayer";
import { carrierLogoUrl } from "@/lib/carrierLogos";

const STAGE_LABEL = { booked: "Booked — awaiting pickup", transit: "In Transit", delivered: "Delivered", won: "Won" };

export default function TruckCard({ rt, risk, onClose, onOpenQuote, onAerial }) {
  if (!rt) return null;
  const color = STAGE_COLORS[rt.stage] || STAGE_COLORS.won;
  const logo = carrierLogoUrl(rt.carrier);
  const side = risk && ((risk.destination?.score || 0) >= (risk.origin?.score || 0) ? risk.destination : risk.origin);
  return (
    <div className="absolute bottom-14 left-3 z-[650] w-[19rem] overflow-hidden rounded-lg border border-white/10 bg-zinc-950/90 text-zinc-100 shadow-2xl backdrop-blur-md animate-fade-up" data-testid="truck-card">
      <div className="flex items-center gap-2 px-3 py-2.5" style={{ borderTop: `2px solid ${color}` }}>
        {logo ? (
          <img src={logo} alt="" className="h-4 w-4 rounded-sm bg-white object-contain p-px"
            onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling.style.display = "block"; }} />
        ) : null}
        <Truck className="h-4 w-4" style={{ color, display: logo ? "none" : "block" }} />
        <span className="font-heading text-sm font-bold">{rt.quote_number}</span>
        <span className="rounded px-1.5 py-0.5 text-[9px] font-bold uppercase" style={{ background: `${color}22`, color }}>
          {STAGE_LABEL[rt.stage] || rt.stage}
        </span>
        <button onClick={onClose} className="ml-auto rounded p-1 text-zinc-500 hover:text-zinc-200" data-testid="truck-card-close">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="space-y-2 px-3 pb-3">
        <div className="flex items-center gap-1.5 text-sm font-semibold">
          <span>{rt.origin.city}, {rt.origin.state}</span>
          <ArrowRight className="h-3.5 w-3.5 text-zinc-500" />
          <span>{rt.destination.city}, {rt.destination.state}</span>
        </div>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
          {rt.customer && rt.customer !== "—" && <div><span className="text-zinc-500">Customer </span><span className="font-semibold">{rt.customer}</span></div>}
          {rt.carrier && <div><span className="text-zinc-500">Carrier </span><span className="font-semibold">{rt.carrier}</span></div>}
          {rt.bol_number && <div><span className="text-zinc-500">{rt.bol_type === "GlobalTranz" ? "BOL " : "PRO "}</span><span className="font-semibold">{rt.bol_number}</span></div>}
          {rt.pickup_date && <div><span className="text-zinc-500">Pickup </span><span className="font-semibold">{rt.pickup_date}</span></div>}
          {rt.stage !== "delivered" && rt.estimated_delivery && <div><span className="text-zinc-500">ETA </span><span className="font-semibold">{rt.estimated_delivery}</span></div>}
          {rt.stage === "delivered" && <div><span className="text-zinc-500">Delivered </span><span className="font-semibold text-emerald-400">{(rt.delivered_at || "").slice(0, 10) || "✓"}</span></div>}
          <div><span className="text-zinc-500">Value </span><span className="font-semibold">{money(rt.booked_cost || rt.value)}</span></div>
        </div>
        {rt.current_location && <div className="text-[11px] text-zinc-400">📍 Near {rt.current_location}</div>}
        {side && side.score > 0 && (
          <div className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-semibold ${
            side.score === 2 ? "bg-red-500/15 text-red-400" : "bg-amber-400/10 text-amber-300"}`} data-testid="truck-card-weather">
            <CloudSun className="h-3.5 w-3.5" /> {side.label} expected {side.date?.slice(5)} · {Math.round(side.temp_hi ?? 0)}°F
          </div>
        )}
        <div className="flex gap-1.5 pt-0.5">
          <button onClick={() => onOpenQuote(rt.id)} data-testid="truck-card-open-quote"
            className="flex flex-1 items-center justify-center gap-1 rounded-md bg-white/10 px-2 py-1.5 text-[11px] font-bold transition-colors hover:bg-white/20">
            <ExternalLink className="h-3 w-3" /> Open quote
          </button>
          <button onClick={() => onAerial(rt)} data-testid="truck-card-aerial"
            className="flex flex-1 items-center justify-center gap-1 rounded-md bg-white/10 px-2 py-1.5 text-[11px] font-bold transition-colors hover:bg-white/20">
            <Video className="h-3 w-3" /> Aerial view
          </button>
        </div>
      </div>
    </div>
  );
}
